import React, { useEffect, useMemo, useState, useRef } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Calendar,
  Filter,
  RotateCw,
  ArrowLeft,
  Flame,
  ChevronDown,
  Users,
  Sparkles,
  Trophy,
  X,
  SlidersHorizontal,
  FileText,
  MonitorPlay,
  LayoutDashboard,
  Maximize,
  Minimize,
} from "lucide-react";
import { db } from "../firebase/firebase";
import { motion, AnimatePresence } from "framer-motion";

/* ---------------------------- Helpers --------------------------- */

const fmt = (n) => new Intl.NumberFormat("en-IN").format(Number(n) || 0);

const shortDate = (d, longYear = false) =>
  new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: longYear ? "numeric" : "2-digit",
  });

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const calculateStreak = (datesArray) => {
  if (!datesArray || datesArray.length === 0) return 0;
  const sorted = [...new Set(datesArray)].sort((a, b) => new Date(b) - new Date(a));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const lastSubmission = new Date(sorted[0]);
  lastSubmission.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - lastSubmission) / (1000 * 60 * 60 * 24));
  if (diffDays > 1) return 0;

  let streak = 1;
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = new Date(sorted[i]);
    const previous = new Date(sorted[i + 1]);
    current.setHours(0, 0, 0, 0);
    previous.setHours(0, 0, 0, 0);
    const difference = Math.floor((current - previous) / (1000 * 60 * 60 * 24));
    if (difference === 1) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
};

/* ------------------------ Small UI pieces ----------------------- */

function StatTile({ icon: Icon, label, value, sub, variant = "light" }) {
  const styles = {
    dark: "bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white border-transparent shadow-md",
    gold: "bg-gradient-to-br from-[#C1A67B] to-[#A88D60] text-white border-transparent shadow-md",
    light: "bg-white text-[#0F172A] border-[#E2E8F0] shadow-sm",
  };

  const textStyles = {
    dark: "text-slate-300",
    gold: "text-white/90",
    light: "text-[#64748B]",
  };

  const iconStyles = {
    dark: "bg-white/10 text-[#C1A67B]",
    gold: "bg-white/20 text-white",
    light: "bg-[#FAFAFA] text-[#94A3B8]",
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border p-3.5 transition-transform hover:-translate-y-0.5 ${styles[variant]}`}>
      <div className="flex items-center justify-between mb-2 relative z-10">
        <p className={`text-[0.6rem] font-bold uppercase tracking-widest ${textStyles[variant]}`}>
          {label}
        </p>
        <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${iconStyles[variant]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      
      <div className="relative z-10 flex flex-col justify-end">
        <p className="font-serif text-2xl font-normal leading-none tabular-nums tracking-tight">
          {value}
        </p>
        {sub && (
          <p className={`mt-1.5 truncate text-[0.65rem] font-medium ${textStyles[variant]}`}>
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

function StreakBadge({ streak, tiny = false }) {
  if (!streak) return <span className="text-[0.65rem] font-medium text-[#CBD5E1]">—</span>;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border border-[#C1A67B]/30 bg-[#C1A67B]/10 font-semibold text-[#A88D60] ${tiny ? "px-1.5 py-[1px] text-[0.55rem]" : "px-2 py-0.5 text-[0.65rem]"}`}>
      <Flame className={tiny ? "h-2 w-2" : "h-3 w-3"} />
      {streak}d
    </span>
  );
}

function RankBadge({ rank }) {
  const podium = rank === 1 ? "bg-gradient-to-br from-[#FDE047] to-[#EAB308] text-[#422006] shadow-lg shadow-yellow-500/20 border border-yellow-300" : 
                 rank === 2 ? "bg-gradient-to-br from-[#E2E8F0] to-[#94A3B8] text-[#0F172A] shadow-md border border-slate-300" : 
                 rank === 3 ? "bg-gradient-to-br from-[#FDBA74] to-[#EA580C] text-[#431407] shadow-md border border-orange-300" : 
                 "bg-[#F1F5F9] text-[#64748B]";
  
  return (
    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[0.65rem] font-bold tabular-nums ${podium}`}>
      {rank}
    </span>
  );
}

function Avatar({ name }) {
  return (
    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#E2E8F0] bg-[#FAFAFA] font-serif text-[0.7rem] font-semibold text-[#0F172A]">
      {initials(name)}
    </div>
  );
}

function HistoryList({ submissions }) {
  if (!submissions || submissions.length === 0) {
    return <p className="text-xs text-[#94A3B8]">No activity found.</p>;
  }
  return (
    <div className="space-y-0">
      {submissions.map((sub, i) => (
        <div key={sub.id ?? i} className="flex items-center gap-3 border-b border-dashed border-[#E2E8F0] py-2.5 last:border-0">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[0.75rem] font-semibold text-[#334155] leading-tight">{sub.type}</p>
            <p className="text-[0.6rem] font-medium uppercase tracking-wider text-[#94A3B8] mt-0.5">{shortDate(sub.date, true)}</p>
          </div>
          <span className="shrink-0 font-serif text-sm tabular-nums text-[#0F172A]">+{fmt(sub.count)}</span>
        </div>
      ))}
    </div>
  );
}

/* --------------------------- Component -------------------------- */

export default function AdminJapa() {
  const navigate = useNavigate();
  const presentationRef = useRef(null);

  const [activeTab, setActiveTab] = useState("admin"); // "admin" | "presentation"
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedDevoteeForLogs, setSelectedDevoteeForLogs] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    loadRecords();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const loadRecords = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      
      const q = query(collection(db, "records"), orderBy("date", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRecords(data);
    } catch (err) {
      console.error(err);
      alert("Unable to load records.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const japaTypes = useMemo(() => [...new Set(records.map((r) => r.type))].filter(Boolean), [records]);

  const allDevoteeHistory = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!r.mobile) return;
      if (!map[r.mobile]) map[r.mobile] = { mobile: r.mobile, name: r.name, dates: new Set() };
      if (r.date) map[r.mobile].dates.add(r.date);
    });
    return map;
  }, [records]);

  const filteredRecords = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return records.filter((record) => {
      const matchesSearch = !searchTerm || record.name?.toLowerCase().includes(searchTerm) || record.mobile?.includes(searchTerm);
      const matchesType = !selectedType || record.type === selectedType;
      const matchesDate = !selectedDate || record.date === selectedDate;
      return matchesSearch && matchesType && matchesDate;
    });
  }, [records, search, selectedType, selectedDate]);

  const filteredDevotees = useMemo(() => {
    const map = {};
    filteredRecords.forEach((r) => {
      if (!r.mobile) return;
      if (!map[r.mobile]) {
        map[r.mobile] = { 
          mobile: r.mobile, 
          name: r.name, 
          totalCount: 0, 
          typeCounts: {}, 
          types: new Set(), 
          dates: new Set(), 
          submissions: [] 
        };
      }
      
      const count = Number(r.count || 0);
      map[r.mobile].totalCount += count;
      
      if (r.type) {
        map[r.mobile].types.add(r.type);
        map[r.mobile].typeCounts[r.type] = (map[r.mobile].typeCounts[r.type] || 0) + count;
      }
      
      if (r.date) map[r.mobile].dates.add(r.date);
      map[r.mobile].submissions.push(r);
    });

    return Object.values(map)
      .map((devotee) => {
        const completeHistory = allDevoteeHistory[devotee.mobile];
        const completeDates = completeHistory ? Array.from(completeHistory.dates) : [];
        return {
          ...devotee,
          types: Array.from(devotee.types),
          streak: calculateStreak(completeDates),
          submissions: devotee.submissions.sort((a, b) => new Date(b.date) - new Date(a.date)),
        };
      })
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [filteredRecords, allDevoteeHistory]);

  const totalDevotees = filteredDevotees.length;
  const overallJapaCount = filteredDevotees.reduce((sum, d) => sum + d.totalCount, 0);
  const topDevotee = filteredDevotees[0];
  const bestStreak = filteredDevotees.reduce((max, d) => Math.max(max, d.streak), 0);
  
  const activeFilters = [
    search && { key: "search", label: `"${search}"`, clear: () => setSearch("") },
    selectedType && { key: "type", label: selectedType, clear: () => setSelectedType("") },
    selectedDate && { key: "date", label: shortDate(selectedDate, true), clear: () => setSelectedDate("") },
  ].filter(Boolean);

  const clearAll = () => { setSearch(""); setSelectedType(""); setSelectedDate(""); };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      presentationRef.current?.requestFullscreen().catch((err) => {
        alert(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const inputClass = "w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 pl-8 text-[0.7rem] text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#C1A67B]";

  return (
    <div className="flex h-[100dvh] w-full flex-col bg-[#FAFAFA] font-sans text-[#1E293B] overflow-hidden">
      <style>{`
        .luxury-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
        .luxury-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .luxury-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
        .luxury-scrollbar::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
        
        .dark-scrollbar::-webkit-scrollbar { width: 6px; }
        .dark-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .dark-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
        .dark-scrollbar::-webkit-scrollbar-thumb:hover { background: #C1A67B; }
      `}</style>

      {/* ------------------------- Header & Tabs ------------------------- */}
      <header className="shrink-0 bg-white z-20 border-b border-[#F1F5F9]">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex items-center justify-between px-4 py-2 sm:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <div>
                <p className="text-[0.55rem] font-bold uppercase tracking-[0.2em] text-[#C1A67B]">
                  Chaturmasya
                </p>
                <h1 className="font-serif text-base text-[#0F172A] leading-tight">
                  Admin Portal
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-[#F1F5F9] p-1 rounded-lg">
              <button
                onClick={() => setActiveTab("admin")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[0.65rem] font-bold uppercase tracking-wider transition-all ${
                  activeTab === "admin" ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"
                }`}
              >
                <LayoutDashboard className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Dashboard</span>
              </button>
              <button
                onClick={() => setActiveTab("presentation")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[0.65rem] font-bold uppercase tracking-wider transition-all ${
                  activeTab === "presentation" ? "bg-[#0F172A] text-[#C1A67B] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"
                }`}
              >
                <MonitorPlay className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Live View</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* =======================================================================
          VIEW 1: ADMIN DASHBOARD (Compact)
          ======================================================================= */}
      {activeTab === "admin" && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* KPIs */}
          <div className="shrink-0 w-full px-4 pt-3 sm:px-6 sm:pt-4 z-10">
            <div className="mx-auto max-w-[1400px] grid grid-cols-2 gap-2 lg:grid-cols-4 sm:gap-3">
              <StatTile variant="dark" icon={Users} label="Total Devotees" value={fmt(totalDevotees)} sub="Participants" />
              <StatTile variant="gold" icon={Sparkles} label="Total Sankalpa" value={fmt(overallJapaCount)} sub={activeFilters.length > 0 ? "Filtered view" : "All offerings"} />
              <StatTile variant="light" icon={Trophy} label="Top Offering" value={topDevotee ? fmt(topDevotee.totalCount) : "0"} sub={topDevotee?.name || "—"} />
              <StatTile variant="light" icon={Flame} label="Best Streak" value={`${bestStreak}d`} sub="Consecutive days" />
            </div>
          </div>

          {/* Filters */}
          <div className="shrink-0 w-full px-4 pt-3 sm:px-6 z-10">
            <div className="mx-auto max-w-[1400px]">
              <div className="flex items-center gap-2 mb-2 lg:hidden">
                <button
                  onClick={() => setFiltersOpen((v) => !v)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-xs font-semibold text-[#0F172A]"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
                </button>
                <button
                  onClick={() => loadRecords(true)}
                  disabled={refreshing || loading}
                  className="flex items-center justify-center gap-1.5 rounded-md border border-[#0F172A] bg-[#0F172A] px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                >
                  <RotateCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </button>
              </div>

              <div className={`flex-col gap-2 ${filtersOpen ? "flex" : "hidden"} lg:flex lg:flex-row lg:items-center`}>
                <div className="flex flex-1 flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#94A3B8]" />
                    <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className={inputClass} />
                  </div>
                  <div className="relative sm:w-40">
                    <Filter className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#94A3B8]" />
                    <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className={`${inputClass} appearance-none cursor-pointer pr-6`}>
                      <option value="">All Offerings</option>
                      {japaTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#94A3B8]" />
                  </div>
                  <div className="relative sm:w-32">
                    <Calendar className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#94A3B8]" />
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className={inputClass} />
                  </div>
                  <div className="hidden lg:flex items-center">
                    <button
                      onClick={() => loadRecords(true)}
                      disabled={refreshing || loading}
                      className="ml-2 flex items-center gap-1.5 rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-[0.65rem] font-bold uppercase tracking-widest text-[#0F172A] hover:bg-[#F8F9FA] disabled:opacity-50"
                    >
                      <RotateCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Syncing" : "Refresh"}
                    </button>
                  </div>
                </div>

                {activeFilters.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="h-4 w-px bg-[#E2E8F0] hidden lg:block mx-1" />
                    {activeFilters.map((f) => (
                      <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1 rounded bg-[#F1F5F9] px-1.5 py-1 text-[0.6rem] font-bold uppercase tracking-wider text-[#475569] hover:bg-[#E2E8F0]">
                        <span className="truncate max-w-[70px]">{f.label}</span>
                        <X className="h-2.5 w-2.5 shrink-0" />
                      </button>
                    ))}
                    <button onClick={clearAll} className="ml-1 text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] hover:text-[#0F172A]">
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table / Cards */}
          <main className="flex-1 min-h-0 w-full px-4 py-3 sm:px-6 sm:py-4">
            <div className="mx-auto max-w-[1400px] h-full flex flex-col bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
              <div className="hidden lg:flex items-center justify-between border-b border-[#E2E8F0] bg-[#FAFAFA] px-4 py-2 shrink-0">
                <h2 className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-[#64748B]">Leaderboard</h2>
                <p className="text-[0.65rem] font-medium text-[#94A3B8]">{fmt(totalDevotees)} Found</p>
              </div>
              <div className="flex-1 overflow-y-auto luxury-scrollbar">
                {loading ? (
                  <div className="flex h-full items-center justify-center text-[0.65rem] font-bold uppercase tracking-widest text-[#94A3B8]">
                    Loading records...
                  </div>
                ) : filteredDevotees.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center p-4">
                    <Search className="h-6 w-6 text-[#E2E8F0]" strokeWidth={1.5} />
                    <p className="mt-3 font-serif text-lg text-[#0F172A]">No Records Found</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden lg:block w-full">
                      <table className="w-full text-left text-sm border-collapse">
                        <thead className="sticky top-0 z-10 bg-[#FAFAFA] border-b border-[#E2E8F0] shadow-sm">
                          <tr>
                            <th className="px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] w-12">#</th>
                            <th className="px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] whitespace-nowrap">Devotee</th>
                            {japaTypes.map(type => (
                              <th key={type} className="px-4 py-2.5 text-right text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] whitespace-nowrap">{type}</th>
                            ))}
                            <th className="px-4 py-2.5 text-center text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] whitespace-nowrap">Streak</th>
                            <th className="px-4 py-2.5 text-right text-[0.6rem] font-bold uppercase tracking-widest text-[#C1A67B] whitespace-nowrap">Overall</th>
                            <th className="px-4 py-2.5 text-right text-[0.6rem] font-bold uppercase tracking-widest text-[#94A3B8] whitespace-nowrap">Logs</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F1F5F9]">
                          {filteredDevotees.map((devotee, idx) => (
                            <tr key={devotee.mobile} className="transition-colors hover:bg-[#F8F9FA] group">
                              <td className="px-4 py-2.5"><RankBadge rank={idx + 1} /></td>
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <div className="flex items-center gap-2.5">
                                  <Avatar name={devotee.name} />
                                  <div>
                                    <p className="font-serif text-[0.95rem] font-medium text-[#0F172A] leading-none">{devotee.name}</p>
                                    <p className="text-[0.6rem] font-semibold tracking-widest text-[#94A3B8] mt-1">+91 {devotee.mobile}</p>
                                  </div>
                                </div>
                              </td>
                              {japaTypes.map(type => (
                                <td key={type} className="px-4 py-2.5 text-right font-serif text-sm text-[#475569] tabular-nums">
                                  {devotee.typeCounts[type] ? fmt(devotee.typeCounts[type]) : <span className="text-[#E2E8F0]">—</span>}
                                </td>
                              ))}
                              <td className="px-4 py-2.5 text-center">
                                <StreakBadge streak={devotee.streak} tiny />
                              </td>
                              <td className="px-4 py-2.5 text-right font-serif text-[1.1rem] font-medium text-[#0F172A] tabular-nums">
                                {fmt(devotee.totalCount)}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  onClick={() => setSelectedDevoteeForLogs(devotee)}
                                  className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-widest text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
                                >
                                  <FileText className="h-2.5 w-2.5" /> Logs
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="divide-y divide-[#F1F5F9] lg:hidden">
                      {filteredDevotees.map((devotee, idx) => (
                        <article key={devotee.mobile} className="p-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <RankBadge rank={idx + 1} />
                              <div className="min-w-0">
                                <h3 className="font-serif text-sm text-[#0F172A] leading-tight truncate">{devotee.name}</h3>
                                <p className="text-[0.55rem] font-semibold tracking-widest text-[#94A3B8] mt-0.5 truncate">+91 {devotee.mobile}</p>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-serif text-lg text-[#0F172A] tabular-nums leading-none">{fmt(devotee.totalCount)}</p>
                              <p className="text-[0.55rem] font-bold uppercase tracking-widest text-[#C1A67B] mt-0.5">Total</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded bg-[#FAFAFA] border border-[#F1F5F9] px-2 py-1.5 mb-2.5">
                            {japaTypes.map(type => {
                              const count = devotee.typeCounts[type];
                              if (!count) return null;
                              return (
                                <div key={type} className="flex items-center gap-1.5">
                                  <span className="text-[0.55rem] font-bold uppercase tracking-widest text-[#94A3B8]">{type}</span>
                                  <span className="font-serif text-[0.8rem] text-[#475569] leading-none tabular-nums">{fmt(count)}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-between">
                            <StreakBadge streak={devotee.streak} tiny />
                            <button
                              onClick={() => setSelectedDevoteeForLogs(devotee)}
                              className="inline-flex items-center gap-1 rounded bg-[#F8F9FA] border border-[#E2E8F0] px-3 py-1 text-[0.6rem] font-bold uppercase tracking-widest text-[#64748B] hover:text-[#0F172A]"
                            >
                              <FileText className="h-2.5 w-2.5" /> Logs
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
      )}

      {/* =======================================================================
          VIEW 2: LIVE PRESENTATION MODE (Broadcast / Stream Ready)
          ======================================================================= */}
      {activeTab === "presentation" && (
        <div 
          ref={presentationRef} 
          className="flex-1 bg-[#0B0F19] flex flex-col overflow-hidden relative"
        >
          {/* Decorative background glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#C1A67B] opacity-[0.03] blur-[120px] rounded-full pointer-events-none" />

          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-8 py-8 md:px-12 relative z-10 border-b border-[#1E293B]/50">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.4em] text-[#C1A67B] mb-2">
                Live Leaderboard
              </p>
              <h1 className="font-serif text-3xl md:text-5xl text-white font-normal tracking-tight">
                Chaturmasya Seva
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-1">Total Offerings</p>
                <p className="font-serif text-3xl text-[#C1A67B] tabular-nums">{fmt(overallJapaCount)}</p>
              </div>
              {!isFullscreen && (
                <button 
                  onClick={toggleFullscreen}
                  className="ml-6 grid h-12 w-12 place-items-center rounded-full bg-[#1E293B] text-slate-300 hover:bg-[#C1A67B] hover:text-[#0B0F19] transition-colors"
                  title="Present Fullscreen"
                >
                  <Maximize className="h-5 w-5" />
                </button>
              )}
              {isFullscreen && (
                <button 
                  onClick={toggleFullscreen}
                  className="ml-6 grid h-12 w-12 place-items-center rounded-full bg-[#1E293B] text-slate-300 hover:bg-white hover:text-[#0B0F19] transition-colors"
                  title="Exit Fullscreen"
                >
                  <Minimize className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* List Area */}
          <div className="flex-1 overflow-y-auto dark-scrollbar px-4 py-8 md:px-12 relative z-10">
            <div className="max-w-5xl mx-auto">
              {loading ? (
                <div className="text-center py-20 text-[#C1A67B] font-serif text-2xl animate-pulse">
                  Syncing Leaderboard...
                </div>
              ) : filteredDevotees.length === 0 ? (
                <div className="text-center py-20 text-slate-500 font-serif text-2xl">
                  No records to display.
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence>
                    {filteredDevotees.slice(0, 15).map((devotee, idx) => (
                      <motion.div
                        layout
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        key={devotee.mobile}
                        className={`flex items-center justify-between p-4 md:p-6 rounded-2xl border ${
                          idx === 0 ? "bg-gradient-to-r from-[#C1A67B]/20 to-transparent border-[#C1A67B]/30" : 
                          idx === 1 ? "bg-slate-800/50 border-slate-700" :
                          idx === 2 ? "bg-orange-900/20 border-orange-800/30" :
                          "bg-[#0F172A]/80 border-[#1E293B]"
                        } backdrop-blur-sm`}
                      >
                        <div className="flex items-center gap-6">
                          <div className={`w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full font-serif text-xl md:text-3xl font-normal shadow-lg ${
                            idx === 0 ? "bg-gradient-to-br from-[#FDE047] to-[#EAB308] text-[#422006] shadow-yellow-500/20" : 
                            idx === 1 ? "bg-gradient-to-br from-[#E2E8F0] to-[#94A3B8] text-[#0F172A]" : 
                            idx === 2 ? "bg-gradient-to-br from-[#FDBA74] to-[#EA580C] text-[#431407]" : 
                            "bg-[#1E293B] text-slate-300"
                          }`}>
                            {idx + 1}
                          </div>
                          
                          <div>
                            <h2 className="font-serif text-2xl md:text-4xl text-white font-normal mb-1">
                              {devotee.name}
                            </h2>
                            {devotee.streak > 0 && (
                              <div className="flex items-center gap-1.5 text-orange-400">
                                <Flame className="h-4 w-4" />
                                <span className="text-xs md:text-sm font-bold uppercase tracking-wider">{devotee.streak} Day Streak</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-[0.65rem] md:text-xs font-bold uppercase tracking-[0.2em] text-[#C1A67B] mb-1">
                            Sankalpa
                          </p>
                          <p className="font-serif text-3xl md:text-5xl text-white tabular-nums">
                            {fmt(devotee.totalCount)}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------- Logs Modal (Admin View) ------------------------- */}
      <AnimatePresence>
        {selectedDevoteeForLogs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedDevoteeForLogs(null)}
            className="fixed inset-0 z-[100] flex flex-col justify-end sm:justify-center items-center bg-[#0F172A]/40 backdrop-blur-sm sm:p-6"
          >
            <motion.div
              initial={{ y: "100%", opacity: 0.5 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-h-[80vh] sm:max-h-[70vh] bg-white rounded-t-2xl sm:rounded-xl sm:max-w-sm flex flex-col shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-[#F1F5F9] px-5 py-3">
                <div>
                  <h3 className="font-serif text-lg text-[#0F172A] leading-tight">
                    {selectedDevoteeForLogs.name}
                  </h3>
                  <p className="text-[0.6rem] font-bold uppercase tracking-widest text-[#C1A67B] mt-1">
                    {selectedDevoteeForLogs.submissions.length} Entries
                  </p>
                </div>
                <button
                  onClick={() => setSelectedDevoteeForLogs(null)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-[#FAFAFA] border border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-1 luxury-scrollbar">
                <HistoryList submissions={selectedDevoteeForLogs.submissions} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}